import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MapContainer, ImageOverlay, Marker, useMapEvents, useMap, ZoomControl, Popup } from 'react-leaflet';
import L from 'leaflet';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/i18n';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Users, Camera, ChevronLeft, ChevronRight, Plus, X, MapPin, Search, Info, Flower2, Sun, Leaf, Snowflake, Shield, Eye, EyeOff, Maximize2, Minimize2, Radio, Mountain, Fuel, Sparkles, Store } from 'lucide-react';
import AddSpotDialog from '@/components/AddSpotDialog';
import SpotDetailDrawer from '@/components/SpotDetailDrawer';
import AuthModal from '@/components/AuthModal';
import LiveCarMeetDialog from '@/components/LiveCarMeetDialog';
import { toast } from 'sonner';

const CAT_SVG = {
  car_meet:    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="7" r="3"/><circle cx="17" cy="7" r="3"/><path d="M2 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/></svg>',
  parking:     '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M9 17V7h4a3 3 0 0 1 0 6H9"/></svg>',
  gas_station: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M3 22V4a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v18"/><path d="M3 12h11"/><path d="M14 8h2a3 3 0 0 1 3 3v6a2 2 0 0 0 4 0V8l-3-3"/></svg>',
  viewpoint:   '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3l4 8 5-5 4 15H3z"/></svg>',
  spot:        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.582a.5.5 0 0 1 0 .962L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/><path d="M20 3v4"/><path d="M22 5h-4"/><path d="M4 17v2"/><path d="M5 18H3"/></svg>',
  s365:        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7"/><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4"/><path d="M2 7h20"/><path d="M22 7v3a2 2 0 0 1-2 2a2.7 2.7 0 0 1-2.5-2a2.7 2.7 0 0 1-5 0a2.7 2.7 0 0 1-5 0A2.7 2.7 0 0 1 4 12a2 2 0 0 1-2-2V7"/></svg>',
};

function makeIcon(category, live = false, hostsLive = false, highlighted = false) {
  const cls = `fz-marker ${category} pulse${live ? ' live-meet' : ''}${hostsLive ? ' host-live' : ''}${highlighted ? ' highlight' : ''}`;
  const inner = CAT_SVG[category] !== undefined ? CAT_SVG[category] : CAT_SVG.spot;
  const badge = hostsLive ? '<span class="fz-host-badge" aria-hidden="true"></span>' : '';
  return L.divIcon({
    className: '',
    html: `<div class="${cls}">${inner}${badge}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

const CATEGORY_ICONS = {
  car_meet: makeIcon('car_meet', true),   // car_meet is now used for live meets only
  parking: makeIcon('parking'),
  gas_station: makeIcon('gas_station'),
  viewpoint: makeIcon('viewpoint'),
  spot: makeIcon('spot'),
  s365: makeIcon('s365'),
};

// Existing data may have category="car_meet" which is now reserved for live meets.
// Render those static spots with the generic "spot" icon to avoid confusion.
function getSpotIcon(category, hostsLive = false, highlighted = false) {
  if (category === 'car_meet') return makeIcon('spot', false, hostsLive, highlighted);
  const known = ['parking', 'gas_station', 'viewpoint', 'spot', 's365'];
  if (!known.includes(category)) return makeIcon('spot', false, hostsLive, highlighted);
  return makeIcon(category, false, hostsLive, highlighted);
}

// Custom inline icon that mirrors the parking marker SVG (rounded square + "P")
const ParkingFilterIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="3" />
    <path d="M9 17V7h4a3 3 0 0 1 0 6H9" />
  </svg>
);

function ClickHandler({ onMapClick, enabled }) {
  useMapEvents({
    click(e) {
      if (!enabled) return;
      onMapClick({ x: e.latlng.lng, y: e.latlng.lat });
    },
  });
  return null;
}

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    map.fitBounds(bounds, { padding: [40, 40], animate: false });
    map.setMaxBounds(bounds.pad(0.4));
  }, [map, bounds]);
  return null;
}

function MapHandle({ onReady }) {
  const map = useMap();
  useEffect(() => { onReady(map); return () => onReady(null); }, [map, onReady]);
  return null;
}

export default function MapView() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [game, setGame] = useState(null);
  const [spots, setSpots] = useState([]);
  const [filters, setFilters] = useState({
    parking: true,
    gas_station: true,
    viewpoint: true,
    spot: true,
    s365: true,
    car_meet: true, // shows LIVE car meets
  });
  const [search, setSearch] = useState('');
  const [addMode, setAddMode] = useState(false);
  const [pendingCoords, setPendingCoords] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [isFs, setIsFs] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);
  const [highlightedSpotId, setHighlightedSpotId] = useState(null);

  const focusSpot = useCallback((s) => {
    if (mapInstance) {
      try { mapInstance.flyTo([s.y, s.x], Math.max(mapInstance.getZoom(), 0), { duration: 0.8 }); }
      catch (_e) { /* ignore */ }
    }
    setHighlightedSpotId(s.id);
    setSelectedSpot(s);
  }, [mapInstance]);

  // Clear highlight after a few seconds
  useEffect(() => {
    if (!highlightedSpotId) return undefined;
    const t = setTimeout(() => setHighlightedSpotId(null), 3500);
    return () => clearTimeout(t);
  }, [highlightedSpotId]);

  // Sync local state with native fullscreen changes (e.g., Esc key)
  useEffect(() => {
    const onChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await (document.documentElement.requestFullscreen?.() ||
          document.documentElement.webkitRequestFullscreen?.());
      } else {
        await (document.exitFullscreen?.() || document.webkitExitFullscreen?.());
      }
    } catch {
      // Fallback: pseudo-fullscreen by toggling padding only
      setIsFs((v) => !v);
    }
  };
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [season, setSeason] = useState('summer');
  const [onlineCount, setOnlineCount] = useState(0);
  const [convoysCount, setConvoysCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [liveMeets, setLiveMeets] = useState([]);
  const [liveMeetOpen, setLiveMeetOpen] = useState(false);
  const [pickingMeet, setPickingMeet] = useState(false); // map click selects meet coords
  const [pendingMeetCoords, setPendingMeetCoords] = useState(null);

  // Online presence poll + heartbeat
  useEffect(() => {
    const fetchOnline = () => {
      api.get('/presence/online').then(({ data }) => setOnlineCount(data.count)).catch(() => {});
      api.get('/convoys').then(({ data }) => setConvoysCount(data.length)).catch(() => {});
    };
    fetchOnline();
    const t1 = setInterval(fetchOnline, 20000);
    if (user && user !== false) {
      api.post('/presence/heartbeat').catch(() => {});
      const t2 = setInterval(() => api.post('/presence/heartbeat').catch(() => {}), 30000);
      return () => { clearInterval(t1); clearInterval(t2); };
    }
    return () => clearInterval(t1);
  }, [user]);

  useEffect(() => {
    api.get(`/games/${gameId}`).then(({ data }) => {
      setGame(data);
      if (data.default_season) setSeason(data.default_season);
    }).catch(() => navigate('/'));
  }, [gameId, navigate]);

  const loadSpots = async () => {
    const { data } = await api.get(`/spots`, { params: { game_id: gameId } });
    setSpots(data);
  };

  useEffect(() => {
    if (!gameId) return undefined;
    loadSpots();
    // Auto-refresh spots so new ones added by other users (or this user from other devices) show up
    const i = setInterval(loadSpots, 25000);
    const onFocus = () => loadSpots();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(i);
      window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  // Online presence poll + heartbeat
  useEffect(() => {
    const fetchOnline = () => api.get('/presence/online').then(({ data }) => setOnlineCount(data.count)).catch(() => {});
    fetchOnline();
    const t1 = setInterval(fetchOnline, 20000);
    if (user && user !== false) {
      api.post('/presence/heartbeat').catch(() => {});
      const t2 = setInterval(() => api.post('/presence/heartbeat').catch(() => {}), 30000);
      return () => { clearInterval(t1); clearInterval(t2); };
    }
    return () => clearInterval(t1);
  }, [user]);

  const bounds = useMemo(() => {
    if (!game) return null;
    return L.latLngBounds([[0, 0], [game.map_height, game.map_width]]);
  }, [game]);

  // Build the set of spot ids that currently host an active live meet
  const spotIdsHostingLive = useMemo(() => {
    const s = new Set();
    for (const lm of liveMeets) if (lm.spot_id) s.add(lm.spot_id);
    return s;
  }, [liveMeets]);

  const filteredSpots = useMemo(() => {
    return spots.filter((s) => {
      const hostsLive = spotIdsHostingLive.has(s.id);
      // Keep spots that host an active live meet visible when the Car Meet (live) filter is on,
      // even if their own category filter is currently disabled.
      const passCategory = filters[s.category] || (hostsLive && filters.car_meet);
      if (!passCategory) return false;
      if (search && !(`${s.title} ${s.description}`.toLowerCase().includes(search.toLowerCase()))) return false;
      return true;
    });
  }, [spots, filters, search, spotIdsHostingLive]);

  // Live car meets — fetched every 30s
  useEffect(() => {
    const fetchLive = () => api.get('/live-meets', { params: { game_id: gameId } })
      .then(({ data }) => setLiveMeets(data)).catch(() => {});
    if (gameId) {
      fetchLive();
      const t1 = setInterval(fetchLive, 30000);
      return () => clearInterval(t1);
    }
    return undefined;
  }, [gameId]);

  const handleMapClick = (coords) => {
    if (pickingMeet) {
      setPendingMeetCoords(coords);
      setPickingMeet(false);
      setLiveMeetOpen(true);
      return;
    }
    if (!user || user === false) {
      setAuthOpen(true);
      toast.message('Connecte-toi pour ajouter un spot.');
      return;
    }
    setPendingCoords(coords);
    setAddOpen(true);
    setAddMode(false);
  };

  const onCreated = (s) => {
    setSpots([s, ...spots]);
  };

  const counts = useMemo(() => ({
    car_meet: liveMeets.length,
    parking: spots.filter((s) => s.category === 'parking').length,
    gas_station: spots.filter((s) => s.category === 'gas_station').length,
    viewpoint: spots.filter((s) => s.category === 'viewpoint').length,
    spot: spots.filter((s) => s.category === 'spot' || s.category === 'car_meet' || s.category === 'photo_spot').length,
    s365: spots.filter((s) => s.category === 's365').length,
  }), [spots, liveMeets]);

  // Map spot.id → list of active live meets pointing to it (for marker badge & detail drawer)
  const liveMeetsBySpot = useMemo(() => {
    const m = {};
    for (const lm of liveMeets) {
      if (lm.spot_id) {
        if (!m[lm.spot_id]) m[lm.spot_id] = [];
        m[lm.spot_id].push(lm);
      }
    }
    return m;
  }, [liveMeets]);

  const seasonUrl = useMemo(() => {
    if (!game) return null;
    if (game.seasons && game.seasons[season]) return game.seasons[season].url;
    return game.map_url;
  }, [game, season]);

  if (!game || !bounds) {
    return (
      <div className="min-h-screen grid place-items-center text-white/60">Chargement de la carte...</div>
    );
  }

  return (
    <div className={`fixed inset-0 ${isFs ? 'pt-0' : 'pt-16'} bg-black ${addMode ? 'fz-adding' : ''} ${pickingMeet ? 'fz-picking-meet' : ''}`}>
      <MapContainer
        crs={L.CRS.Simple}
        bounds={bounds}
        maxBounds={bounds.pad(0.3)}
        minZoom={-3}
        maxZoom={3}
        zoomSnap={0.25}
        zoomDelta={0.5}
        wheelPxPerZoomLevel={120}
        style={{ height: '100%', width: '100%' }}
        attributionControl={false}
        zoomControl={false}
      >
        <ZoomControl position="topright" />
        <FitBounds bounds={bounds} />
        <MapHandle onReady={setMapInstance} />
        <ImageOverlay key={season} url={seasonUrl} bounds={bounds} opacity={1} />
        <ClickHandler onMapClick={handleMapClick} enabled={addMode || pickingMeet} />
        {filteredSpots.map((s) => (
          <Marker
            key={`${s.id}-${highlightedSpotId === s.id ? 'h' : 'n'}-${liveMeetsBySpot[s.id]?.length || 0}`}
            position={[s.y, s.x]}
            icon={getSpotIcon(s.category, !!liveMeetsBySpot[s.id]?.length, highlightedSpotId === s.id)}
            eventHandlers={{ click: () => setSelectedSpot(s) }}
          />
        ))}
        {filters.car_meet && liveMeets.filter((m) => !m.spot_id).map((m) => {
          const isOwner = user && user !== false && user.id === m.user_id;
          const isAdmin = user && user !== false && user.role === 'admin';
          const canDelete = isOwner || isAdmin;
          return (
            <Marker
              key={`live-${m.id}`}
              position={[m.y, m.x]}
              icon={CATEGORY_ICONS.car_meet}
            >
              <Popup className="fz-live-popup" closeButton={false} minWidth={240}>
                <div data-testid={`livemeet-popup-${m.id}`} className="text-white" style={{ minWidth: 240 }}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF007F] text-white text-[9px] font-black uppercase tracking-widest">
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-[#00F0FF] font-bold">Car Meet</span>
                  </div>
                  <div className="font-display font-black text-base leading-tight uppercase tracking-tight mb-1.5">
                    {m.title}
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    {m.avatar_url ? (
                      <img src={m.avatar_url} alt={m.user_name} className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#FF007F] to-[#00F0FF] grid place-items-center text-black font-bold text-[10px]">
                        {(m.user_name || '?')[0].toUpperCase()}
                      </div>
                    )}
                    <Link
                      to={`/u/${m.user_id}`}
                      data-testid={`livemeet-host-${m.id}`}
                      className="text-sm font-semibold hover:text-[#00F0FF] transition"
                    >
                      {m.user_name}
                    </Link>
                  </div>
                  {m.convoy_id && (
                    <div className="mb-3 px-2.5 py-2 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/20" data-testid={`livemeet-convoy-${m.id}`}>
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#00F0FF] font-bold mb-1">
                        <Users className="w-3 h-3" /> {t('livemeet.linked_convoy')}
                      </div>
                      <Link
                        to={`/convoys/${m.convoy_id}`}
                        className="text-xs font-bold hover:text-[#00F0FF] transition block"
                      >
                        {m.convoy_name}
                      </Link>
                      <div className="text-[10px] text-white/50 mt-0.5">
                        {m.convoy_members_count}/{m.convoy_max_size} {t('convoys.members')}
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col gap-2">
                    {m.convoy_id && !isOwner && (() => {
                      const isFull = m.convoy_members_count >= m.convoy_max_size;
                      return (
                        <button
                          type="button"
                          data-testid={`livemeet-join-convoy-${m.id}`}
                          disabled={isFull}
                          onClick={async () => {
                            if (!user || user === false) { setAuthOpen(true); return; }
                            try {
                              await api.post(`/convoys/${m.convoy_id}/join`);
                              toast.success(t('livemeet.joined_convoy'));
                              navigate(`/convoys/${m.convoy_id}`);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || t('common.error'));
                            }
                          }}
                          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider transition bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Users className="w-3.5 h-3.5" /> {isFull ? t('livemeet.convoy_full') : t('livemeet.join_convoy')}
                        </button>
                      );
                    })()}
                    {canDelete && (
                      <button
                        type="button"
                        data-testid={`livemeet-delete-${m.id}`}
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          try {
                            await api.delete(`/live-meets/${m.id}`);
                            setLiveMeets((prev) => prev.filter((x) => x.id !== m.id));
                            toast.success(isOwner ? t('livemeet.stopped') : t('livemeet.deleted'));
                          } catch (err) {
                            toast.error(err.response?.data?.detail || t('common.error'));
                          }
                        }}
                        className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider transition ${
                          isAdmin && !isOwner
                            ? 'bg-[#FF007F] hover:bg-[#FF007F]/90 text-white'
                            : 'bg-white/10 hover:bg-red-500/20 text-white/80 hover:text-red-200'
                        }`}
                      >
                        {isAdmin && !isOwner ? <Shield className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        {isAdmin && !isOwner ? t('livemeet.admin_delete') : t('livemeet.stop')}
                      </button>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Fullscreen toggle - aligned in same column as Leaflet zoom (topright) */}
      <button
        type="button"
        data-testid="map-fullscreen-btn"
        onClick={toggleFullscreen}
        title={isFs ? t('map.exit_fullscreen') : t('map.fullscreen')}
        className={`absolute right-[10px] ${isFs ? 'top-[80px]' : 'top-[140px]'} z-[1000] w-[30px] h-[30px] grid place-items-center bg-white text-black rounded-md border border-black/20 shadow hover:bg-[#00F0FF]/90 hover:text-black transition`}
      >
        {isFs ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
      </button>

      {/* Online presence + convoys indicators - top center */}
      <div className="absolute top-20 left-1/2 -translate-x-1/2 z-[1000] flex items-center gap-2" data-testid="map-online-indicator">
        <div className="glass rounded-full px-4 py-2 inline-flex items-center gap-2 text-xs">
          <span className="relative flex w-2 h-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
          </span>
          <span className="font-bold text-white">{onlineCount}</span>
          <span className="text-white/60">{t('map.online_now')}</span>
        </div>
        <Link
          to="/convoys"
          data-testid="map-convoys-indicator"
          className="glass rounded-full px-4 py-2 inline-flex items-center gap-2 text-xs hover:bg-[#FF007F]/10 transition border border-transparent hover:border-[#FF007F]/30"
          title={t('map.live_convoys')}
        >
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-none stroke-[#FF007F]" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="2"/>
            <path d="M16.24 7.76a6 6 0 0 1 0 8.49M19.07 4.93a10 10 0 0 1 0 14.14M7.76 16.24a6 6 0 0 1 0-8.49M4.93 19.07a10 10 0 0 1 0-14.14"/>
          </svg>
          <span className="font-bold text-white">{convoysCount}</span>
          <span className="text-white/60">{t('map.live_convoys')}</span>
        </Link>
      </div>

      {/* LEFT SIDEBAR - Filters (collapsible) */}
      {sidebarOpen ? (
        <aside className="absolute top-20 left-4 w-64 max-h-[calc(100vh-16rem)] glass rounded-2xl p-3 overflow-y-auto z-[1000]" data-testid="filter-sidebar">          <button
            type="button"
            data-testid="sidebar-hide-btn"
            onClick={() => setSidebarOpen(false)}
            className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 grid place-items-center text-white/60 hover:text-white transition"
            title={t('map.hide_sidebar')}
          >
            <EyeOff className="w-3.5 h-3.5" />
          </button>        <Link to="/" className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white">
          <ChevronLeft className="w-3 h-3" /> {t('map.back_home')}
        </Link>
        <h2 className="font-display font-black text-2xl uppercase tracking-tighter mt-3 leading-none">
          {game.title}
        </h2>
        <div className="text-xs text-white/50 mt-1">{game.location} · {game.year}</div>

        <div className="mt-5 relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-white/40" />
          <Input
            data-testid="filter-search"
            placeholder={t('map.search_placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-black/40 border-white/10 text-white placeholder:text-white/30 pl-9"
          />
        </div>

        <div className="mt-5 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-semibold">{t('map.filters')}</div>
            <button
              type="button"
              data-testid="filter-toggle-all"
              onClick={() => {
                const allOn = Object.values(filters).every(Boolean);
                const next = Object.fromEntries(Object.keys(filters).map((k) => [k, !allOn]));
                setFilters(next);
              }}
              className="text-[10px] uppercase tracking-wider font-bold text-[#00F0FF] hover:text-white transition"
            >
              {Object.values(filters).every(Boolean)
                ? (t('map.hide_all') || 'Tout masquer')
                : (t('map.show_all') || 'Tout afficher')}
            </button>
          </div>

          {[
            { key: 'car_meet', label: 'map.cat.live_carmeet', icon: Radio, color: '#00F0FF', live: true },
            { key: 'viewpoint', label: 'map.cat.viewpoint', icon: Mountain, color: '#9DEDFF' },
            { key: 'parking', label: 'map.cat.parking', icon: ParkingFilterIcon, color: '#FFD479' },
            { key: 'gas_station', label: 'map.cat.gas', icon: Fuel, color: '#10B981' },
            { key: 's365', label: 'map.cat.s365', icon: Store, color: '#FF7A00' },
            { key: 'spot', label: 'map.cat.spot', icon: Sparkles, color: '#A78BFA' },
          ].map((f) => {
            const Icon = f.icon;
            const n = counts[f.key] || 0;
            return (
              <div key={f.key} className="flex items-center justify-between p-2.5 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-7 h-7 rounded-full grid place-items-center flex-shrink-0" style={{ background: `${f.color}22`, color: f.color }}>
                    <Icon className={`w-3.5 h-3.5 ${f.live ? 'animate-pulse' : ''}`} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-bold leading-tight truncate">{t(f.label)}</div>
                    <div className="text-[10px] text-white/50">{n} {f.live ? t('map.live') : 'spot' + (n > 1 ? 's' : '')}</div>
                  </div>
                </div>
                <Switch
                  data-testid={`filter-toggle-${f.key}`}
                  checked={filters[f.key]}
                  onCheckedChange={(v) => setFilters({ ...filters, [f.key]: v })}
                />
              </div>
            );
          })}
        </div>

        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-semibold mb-2">
            {t('map.spots_count')} ({filteredSpots.length})
          </div>
          <div className="space-y-1 max-h-72 overflow-y-auto no-scrollbar">
            {filteredSpots.map((s) => {
              const catColor = ({
                car_meet: '#00F0FF', parking: '#FFD479', gas_station: '#10B981',
                viewpoint: '#9DEDFF', spot: '#A78BFA', s365: '#FF7A00',
              })[s.category] || '#A78BFA';
              const isHighlighted = highlightedSpotId === s.id;
              return (
                <button
                  key={s.id}
                  data-testid={`spot-list-${s.id}`}
                  onClick={() => focusSpot(s)}
                  className={`w-full text-left p-2.5 rounded-lg border transition-all ${
                    isHighlighted
                      ? 'bg-white/10 border-white/30 ring-1 ring-white/20'
                      : 'border-transparent hover:bg-white/5 hover:border-white/10'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: catColor, boxShadow: `0 0 6px ${catColor}` }} />
                    <span className="text-sm font-semibold truncate">{s.title}</span>
                  </div>
                  <div className="text-[11px] text-white/40 ml-4 truncate">{t('map.by') || 'par'} {s.author_name} · ♥ {s.likes_count}</div>
                </button>
              );
            })}
            {filteredSpots.length === 0 && (
              <div className="text-xs text-white/40 px-2 py-3 text-center">
                {user && user !== false ? t('map.empty_logged_in') : t('map.empty_logged_out')}
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 p-3 rounded-xl bg-white/5 border border-white/10 text-xs text-white/60 flex gap-2">
          <Info className="w-4 h-4 flex-shrink-0 text-[#00F0FF]" />
          <span>
            {t('map.tip').split(t('map.tip_strong'))[0]}<strong className="text-white">{t('map.tip_strong')}</strong>{t('map.tip').split(t('map.tip_strong'))[1] || ''}
          </span>
        </div>
      </aside>
      ) : (
        <button
          type="button"
          data-testid="sidebar-show-btn"
          onClick={() => setSidebarOpen(true)}
          className="absolute top-20 left-4 z-[1000] glass rounded-full pl-2 pr-3 py-2 inline-flex items-center gap-1.5 text-xs text-white/80 hover:text-white hover:bg-white/5 transition"
          title={t('map.show_sidebar')}
        >
          <Eye className="w-3.5 h-3.5" />
          <ChevronRight className="w-3 h-3" />
          <span className="font-bold uppercase tracking-wider">{t('map.filters')}</span>
        </button>
      )}

      {/* FLOATING ACTION BUTTONS - bottom-right (above Emergent badge) */}
      <div className="absolute bottom-24 right-4 z-[1000] flex flex-col items-end gap-3">
        {pickingMeet && (
          <div className="glass rounded-full px-4 py-2 text-xs text-[#00F0FF] animate-pulse inline-flex items-center gap-2" data-testid="livemeet-pick-banner">
            <Radio className="w-3.5 h-3.5" /> {t('livemeet.pick_banner')}
          </div>
        )}
        {addMode && (
          <div className="glass rounded-full px-4 py-2 text-xs text-white/80 animate-pulse">
            Clique sur la carte pour placer ton spot
          </div>
        )}
        <Button
          data-testid="livemeet-toggle-btn"
          onClick={() => {
            if (!user || user === false) { setAuthOpen(true); return; }
            setAddMode(false);
            if (pickingMeet) { setPickingMeet(false); return; }
            // New flow: pick location FIRST, then open dialog
            setPendingMeetCoords(null);
            setPickingMeet(true);
            toast.message(t('livemeet.pick_banner'));
          }}
          className="rounded-full h-11 px-5 shadow-2xl font-bold uppercase tracking-wide bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black"
        >
          <Radio className="w-4 h-4 mr-2" /> {t('map.signal_carmeet')}
        </Button>
        <Button
          data-testid="addspot-toggle-btn"
          onClick={() => {
            if (!user || user === false) { setAuthOpen(true); return; }
            setPickingMeet(false);
            setAddMode(!addMode);
          }}
          className={`rounded-full h-12 px-6 shadow-2xl font-bold uppercase tracking-wide ${
            addMode
              ? 'bg-white text-black hover:bg-white/90'
              : 'bg-[#FF007F] hover:bg-[#FF007F]/90 text-white'
          }`}
        >
          {addMode ? <><X className="w-5 h-5 mr-2" /> {t('map.cancel')}</> : <><Plus className="w-5 h-5 mr-2" /> {t('map.add_spot')}</>}
        </Button>
      </div>

      {/* Legend + Season switcher bottom-left */}
      <div className="absolute bottom-6 left-4 z-[1000] flex flex-col items-start gap-2">
        <div className="glass rounded-2xl px-3 py-2 inline-flex items-center gap-2 self-start" data-testid="season-switcher">
          {[
            { id: 'spring', icon: Flower2, color: '#86EFAC', label: t('common.season.spring') },
            { id: 'summer', icon: Sun, color: '#FFD479', label: t('common.season.summer') },
            { id: 'autumn', icon: Leaf, color: '#FB923C', label: t('common.season.autumn') },
            { id: 'winter', icon: Snowflake, color: '#7DD3FC', label: t('common.season.winter') },
          ].map((s) => {
            const active = season === s.id;
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                type="button"
                data-testid={`season-btn-${s.id}`}
                onClick={() => setSeason(s.id)}
                title={s.label}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all text-xs font-bold uppercase tracking-wider ${
                  active
                    ? 'bg-white text-black shadow-[0_0_24px_rgba(255,255,255,0.25)]'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
                style={active ? {} : { color: 'rgba(255,255,255,0.55)' }}
              >
                <Icon className="w-3.5 h-3.5" style={!active ? { color: s.color } : {}} />
                <span className="hidden sm:inline">{s.label}</span>
              </button>
            );
          })}
        </div>

        <div className="glass rounded-2xl px-3 py-2 flex items-center gap-3 text-[11px] flex-wrap max-w-[min(90vw,720px)]" data-testid="map-legend">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#00F0FF', boxShadow: '0 0 10px #00F0FF' }} />
            {t('map.legend.carmeet')}
          </div>
          <div className="w-px h-3.5 bg-white/10" />
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#9DEDFF', boxShadow: '0 0 10px #9DEDFF' }} />
            {t('map.cat.viewpoint')}
          </div>
          <div className="w-px h-3.5 bg-white/10" />
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#FFD479', boxShadow: '0 0 10px #FFD479' }} />
            {t('map.cat.parking')}
          </div>
          <div className="w-px h-3.5 bg-white/10" />
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#10B981', boxShadow: '0 0 10px #10B981' }} />
            {t('map.cat.gas')}
          </div>
          <div className="w-px h-3.5 bg-white/10" />
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#FF7A00', boxShadow: '0 0 10px #FF7A00' }} />
            {t('map.cat.s365')}
          </div>
          <div className="w-px h-3.5 bg-white/10" />
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#A78BFA', boxShadow: '0 0 10px #A78BFA' }} />
            {t('map.cat.spot')}
          </div>
          {user && user !== false && user.role === 'admin' && (
            <>
              <div className="w-px h-3.5 bg-white/10" />
              <Link
                to="/admin"
                data-testid="link-admin"
                className="inline-flex items-center gap-1 text-[#FF007F] hover:text-white transition"
              >
                <Shield className="w-3 h-3" /> Admin
              </Link>
            </>
          )}
        </div>
      </div>

      <AddSpotDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        gameId={gameId}
        coords={pendingCoords}
        onCreated={onCreated}
      />

      <SpotDetailDrawer
        spot={selectedSpot}
        liveMeetsForSpot={selectedSpot ? (liveMeetsBySpot[selectedSpot.id] || []) : []}
        onLiveMeetStopped={(meetId) => setLiveMeets((prev) => prev.filter((m) => m.id !== meetId))}
        onStartLiveMeet={() => {
          if (!selectedSpot) return;
          if (!user || user === false) { setAuthOpen(true); return; }
          setPendingMeetCoords({ x: selectedSpot.x, y: selectedSpot.y, spot_id: selectedSpot.id });
          setLiveMeetOpen(true);
        }}
        onClose={() => setSelectedSpot(null)}
        onUpdated={(updated) => {
          setSelectedSpot(updated);
          setSpots(spots.map((s) => s.id === updated.id ? updated : s));
        }}
        onDeleted={(id) => {
          setSpots(spots.filter((s) => s.id !== id));
          setSelectedSpot(null);
        }}
        onRequireAuth={() => setAuthOpen(true)}
      />

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />

      <LiveCarMeetDialog
        open={liveMeetOpen}
        onOpenChange={setLiveMeetOpen}
        coords={pendingMeetCoords}
        onCreated={(m) => setLiveMeets((prev) => [m, ...prev.filter((x) => x.user_id !== m.user_id)])}
        onRequestPickCoords={() => { setLiveMeetOpen(false); setAddMode(false); setPickingMeet(true); }}
      />
    </div>
  );
}
